import { AxiosError } from 'axios'
import { trim } from 'lodash'
import { toast } from 'react-toastify'
import * as yup from 'yup'

import { EActionsKey, THeadCellWithActions } from '~/components/table/models'
import { NEW_LINE } from '~/constants'
import {
  ECampaignChannel,
  ECampaignContactListItemKeys,
  ECampaignKeys,
  ECampaignTabs,
  TCampaignError,
  TCampaignGetContactListItem,
  TCampaignListItem,
  TMergeField,
  TVariableToColumnMap,
} from '~/features/campaigns/models'
import {
  CAMPAIGN_DEFAULT_MERGE_FIELDS,
  INSERT_INPUT_MAX_LENGTH,
} from '~/features/campaigns/utils/constants'
import { EClientPath, EServerPath } from '~/models/api'
import pathBuilder from '~/utils/api'

export const getFilteredCampaigns = (
  searchValue: string,
  data: TCampaignListItem[] | null | undefined,
) =>
  (data || []).filter(
    (campaign) =>
      campaign &&
      (campaign.campaignName
        ?.toLowerCase()
        .includes(searchValue.toLowerCase()) ||
        campaign.status?.toLowerCase().includes(searchValue.toLowerCase()) ||
        campaign.channel?.toLowerCase().includes(searchValue.toLowerCase()) ||
        campaign.createdDate
          ?.toLowerCase()
          .includes(searchValue.toLowerCase())),
  )

export const queryKeys = {
  campaigns: (agentId: string) => [EServerPath.campaigns, agentId],
  campaign: (id: string) => [EServerPath.campaign, id],
  campaignExecute: (id: string) => [EServerPath.campaignExecute, id],
  campaignContact: (id: string, campaignContactId: string) => [
    EServerPath.campaignContact,
    id,
    campaignContactId,
  ],
  campaignContacts: (id: string) => [EServerPath.campaignContacts, id],
  campaignConversations: (id: string) => [
    EServerPath.campaignConversations,
    id,
  ],
}

export const getTabFromRoute = (pathname: string, id: string) => {
  switch (pathname) {
    case pathBuilder(EClientPath.campaignContacts, { id }):
    case pathBuilder(EClientPath.campaignOfuContacts, { id }):
      return ECampaignTabs.contacts
    case pathBuilder(EClientPath.campaignResults, { id }):
      return ECampaignTabs.results
    case pathBuilder(EClientPath.campaignDetails, { id }):
    case pathBuilder(EClientPath.campaignOfuDetails, { id }):
    default:
      return ECampaignTabs.details
  }
}

export const parseCsvRow = (row: string): string[] => {
  const result: string[] = []
  let inQuotes = false
  let value = ''

  for (let i = 0; i < row.length; i += 1) {
    const char = row[i]

    if (char === '"') {
      inQuotes = !inQuotes
    } else if (char === ',' && !inQuotes) {
      // If we hit a comma and we're not inside quotes, push the value to the result
      result.push(value.trim())
      value = ''
    } else {
      value += char
    }
  }

  result.push(value.trim())

  return result
}

export const campaignValidationSchema = yup.object().shape({
  [ECampaignKeys.introMessage]: yup
    .string()
    .max(INSERT_INPUT_MAX_LENGTH)
    .required('Intro message is required'),
  [ECampaignKeys.voicemailMessage]: yup
    .string()
    .max(INSERT_INPUT_MAX_LENGTH)
    .required('Voicemail message is required'),
  [ECampaignKeys.campaignName]: yup
    .string()
    .required('Campaign name is required'),
  [ECampaignKeys.questions]: yup
    .string()
    .trim()
    .required('Conversation Questions are required'),
})

export const ofuCampaignValidationSchema = yup.object().shape({
  [ECampaignKeys.id]: yup.string(),
  [ECampaignKeys.agentId]: yup.string(),
  [ECampaignKeys.campaignName]: yup
    .string()
    .required('Campaign name is required'),
  [ECampaignKeys.campaignType]: yup.string().required(),
  [ECampaignKeys.channel]: yup.string().required(),
  [ECampaignKeys.status]: yup.string(),
  [ECampaignKeys.createdDate]: yup.date(),
  [ECampaignKeys.updatedDate]: yup.date(),
  [ECampaignKeys.ofuGoal]: yup.string().required('Goal is required'),
  [ECampaignKeys.ofuQualificationCriteria]: yup
    .string()
    .required('Qualification criteria is required'),
  [ECampaignKeys.startDate]: yup.date().required('Start date is required'),
  [ECampaignKeys.endDate]: yup.date().nullable(),
  [ECampaignKeys.ofuOutreachTime]: yup.string().nullable(),
  [ECampaignKeys.ofuIsManual]: yup.boolean(),
  [ECampaignKeys.ofuMaxReachouts]: yup
    .number()
    .min(1)
    .max(20)
    .required('Max reachouts is required'),
  [ECampaignKeys.ofuCadence]: yup.string().required('Cadence is required'),
  [ECampaignKeys.ofuChannels]: yup
    .array()
    .min(1, 'Select at least one channel')
    .required('Channels are required'),
  [ECampaignKeys.ofuCadenceCustom]: yup.string().nullable(),
  [ECampaignKeys.ofuCustomEmailService]: yup
    .string()
    .url('Must be a valid URL')
    .nullable(),
  [ECampaignKeys.ofuCustomEmailBody]: yup
    .mixed()
    .test('is-valid-json', 'Must be a valid JSON object', (value) => {
      if (!value) return true // Allow null/undefined
      if (typeof value === 'object') return true // Allow objects
      try {
        const parsed = JSON.parse(value as string)
        return typeof parsed === 'object' && parsed !== null
      } catch {
        return false
      }
    })
    .nullable(),
})

export const getArrayFromStringList = (
  value: string | null | undefined,
): string[] | undefined => {
  const resultArray = (value || '')
    .replaceAll(/• /g, '')
    .split(NEW_LINE)
    .map(trim)
    .filter(Boolean)

  return resultArray.length > 0 ? resultArray : undefined
}

export const getStringListFromArray = (
  value: string[] | null | undefined,
): string =>
  (value || [])
    .map((line) => {
      if (line.startsWith('• ') || line === '•') {
        return line
      }

      if (line.startsWith('•')) {
        return `• ${line.slice(1)}`
      }

      return `• ${line}`
    })
    .filter((line) => line !== '•')
    .join(NEW_LINE)

export const getRequiredMergeFields = (channel: ECampaignChannel) => {
  const result: string[] = [CAMPAIGN_DEFAULT_MERGE_FIELDS.FIRST_NAME]

  switch (channel) {
    case ECampaignChannel.email:
      result.push(CAMPAIGN_DEFAULT_MERGE_FIELDS.EMAIL)
      break
    case ECampaignChannel.text:
    case ECampaignChannel.phone:
    default:
      result.push(CAMPAIGN_DEFAULT_MERGE_FIELDS.PHONE_NUMBER)
  }

  return result.map(
    (field) => ({ friendlyName: field, required: true }) as TMergeField,
  )
}

export const getMergeFields = (
  existingMergeFields: TMergeField[] | null | undefined,
  channel: ECampaignChannel,
) => {
  const result: TMergeField[] = (existingMergeFields || []).map((field) => {
    switch (field.friendlyName) {
      case CAMPAIGN_DEFAULT_MERGE_FIELDS.FIRST_NAME:
        return {
          ...field,
          required: true,
          variable: field.variable || (undefined as never),
        }
      case CAMPAIGN_DEFAULT_MERGE_FIELDS.EMAIL:
        return {
          ...field,
          required: channel === ECampaignChannel.email,
          variable: field.variable || (undefined as never),
        }
      case CAMPAIGN_DEFAULT_MERGE_FIELDS.PHONE_NUMBER:
        return {
          ...field,
          required: channel !== ECampaignChannel.email,
          variable: field.variable || (undefined as never),
        }
      default:
        return {
          ...field,
          required: false,
          variable: field.variable || (undefined as never),
        }
    }
  })

  const requiredMergeFields = getRequiredMergeFields(channel)
  const missingRequiredFields = requiredMergeFields.filter(
    (requiredField) =>
      !result.some(
        (resultField) =>
          resultField.friendlyName === requiredField.friendlyName,
      ),
  )

  if (missingRequiredFields.length) {
    result.unshift(...missingRequiredFields)
  }

  return result
}

export const extractFriendlyErrorMessage = (
  payload: TCampaignError,
): string => {
  const { message, error } = payload
  const { formErrors = [], fieldErrors = {} } = error || {}

  let errorMessage = message

  // If form errors exist, add them to the errorMessage
  if (formErrors?.length > 0) {
    errorMessage += `\nForm Errors:\n- ${formErrors?.join('\n- ')}`
  }

  // If field errors exist, add them to the errorMessage
  if (Object.keys(fieldErrors || {}).length > 0) {
    errorMessage += `\nField Errors:`
    Object.entries(fieldErrors || {}).forEach(([field, errors]) => {
      const uniqueErrors = Array.from(new Set(errors))
      errorMessage += `\n- ${field}: ${uniqueErrors.join(', ')}`
    })
  }

  return errorMessage
}

export const getContactValueByVariable = (
  variableToColumnMap: TVariableToColumnMap[],
  contact: Record<string, string>,
  variable: string,
  autoParsedColumnTitles?: {
    columnTitle: string
    dbFieldConventionalName: string
  }[],
) => {
  const mapItem = variableToColumnMap.find((item) => item.variable === variable)

  const keyName = mapItem?.column

  if (!keyName && !autoParsedColumnTitles) {
    return undefined as never
  }

  const autoParsedColumnTitle = (autoParsedColumnTitles || []).find(
    (field) => field.dbFieldConventionalName === variable,
  )?.columnTitle

  if (autoParsedColumnTitle) {
    return contact[autoParsedColumnTitle] || (undefined as never)
  }

  return contact[keyName!] || (undefined as never)
}

export const hasSimilarities = (
  variableByUser: string,
  csvColumnTitle: string,
  ourTarget: string,
) => {
  const lowercaseVariableByUser = variableByUser
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]/g, '')
  const lowercaseCsvColumnTitle = csvColumnTitle
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]/g, '')
  const lowercaseOurTarget = ourTarget
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]/g, '')
  const ourPartialTarget = ourTarget
    .split(' ')[0]
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]/g, '')

  return (
    lowercaseOurTarget.includes(lowercaseVariableByUser) ||
    lowercaseOurTarget.includes(lowercaseCsvColumnTitle) ||
    lowercaseVariableByUser.includes(lowercaseOurTarget) ||
    lowercaseCsvColumnTitle.includes(lowercaseOurTarget) ||
    ourPartialTarget.includes(lowercaseVariableByUser) ||
    ourPartialTarget.includes(lowercaseCsvColumnTitle) ||
    lowercaseVariableByUser.includes(ourPartialTarget) ||
    lowercaseCsvColumnTitle.includes(ourPartialTarget)
  )
}

export const showToastOnCampaignErrorResponse = (
  result: Error,
  defaultMessage: string,
) => {
  try {
    const errorData = (result as AxiosError).response?.data as TCampaignError

    if (errorData.error) {
      toast.error(`${defaultMessage} ${extractFriendlyErrorMessage(errorData)}`)
    }
  } catch {
    toast.error(defaultMessage)
  }
}

export const getCampaignContactsHeadCells = (
  isNoSmallScreen: boolean,
): THeadCellWithActions<TCampaignGetContactListItem> =>
  [
    {
      id: ECampaignContactListItemKeys.firstName,
      label: 'First Name',
      isSortable: true,
    },
    {
      id: ECampaignContactListItemKeys.phone,
      label: 'Phone',
      colSpan: isNoSmallScreen ? 1 : 2,
      isSortable: true,
    },
    {
      id: ECampaignContactListItemKeys.customVariables,
      label: 'Custom Variables',
    },
    {
      id: ECampaignContactListItemKeys.preview,
      label: 'Preview',
    },
    {
      id: ECampaignContactListItemKeys.status,
      label: 'Status',
      isSortable: true,
    },
    isNoSmallScreen
      ? {
          id: ECampaignContactListItemKeys.testContact,
          label: 'Test Contact',
        }
      : null,
    isNoSmallScreen
      ? {
          id: ECampaignContactListItemKeys.createdDate,
          label: 'Added date',
          isSortable: true,
        }
      : null,
    {
      id: EActionsKey.actions,
      label: 'Actions',
    },
  ].filter(Boolean) as THeadCellWithActions<TCampaignGetContactListItem>

export function stripExtraQuotes(input?: string | null) {
  if (!input) return undefined
  const stripped = input.replace(/^['"](.*)['"]$/, '$1')
  // eslint-disable-next-line consistent-return
  return stripped === '' ? undefined : stripped
}
